import type { AnimalItem } from "../mocks/animals";
import type { PaymentRecord } from "../mocks/payments";
import type { IncomeExpenseEntry } from "./incomeExpenseStore";
import type { SharePresaleRecord } from "./sharePresalesStore";
import { getDisplayKupeNo, getDisplaySiparisNo } from "./animalLabels";
import { getShareTotal } from "./cascadeOps";
import type {
  CustomerDebtRow,
  ReportFilters,
  ReportsAggregateResult,
  SaleReportRow,
  UserPerformanceRow,
} from "./reportAggregations";

interface SheetColumn<T> {
  label: string;
  type?: "string" | "number" | "money" | "date";
  width?: number;
  value: (row: T) => string | number | boolean | null | undefined;
}

interface SheetDefinition<T> {
  name: string;
  title: string;
  subtitle?: string;
  columns: SheetColumn<T>[];
  rows: T[];
}

function xmlEscape(value: unknown) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/\r?\n/g, " &#10; ");
}

function normalizeFileName(value: string) {
  return value
    .toLocaleLowerCase("tr-TR")
    .replace(/[^a-z0-9çğıöşü\s_-]/gi, "")
    .trim()
    .replace(/\s+/g, "-");
}

function formatDateLabel(value?: string) {
  const normalized = String(value ?? "").slice(0, 10);
  if (!normalized) return "-";
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return normalized;
  return `${day}.${month}.${year}`;
}

function getActiveFiltersLabel(filters: ReportFilters) {
  const parts: string[] = [];

  if (filters.kupeNo.trim()) parts.push(`Küpe: ${filters.kupeNo.trim()}`);
  if (filters.customer.trim()) parts.push(`Müşteri: ${filters.customer.trim()}`);
  if (filters.user.trim()) parts.push(`Kullanıcı: ${filters.user.trim()}`);
  if (filters.salesType !== "Tümü") parts.push(`Satış Tipi: ${filters.salesType}`);
  if (filters.paymentStatus !== "Tümü") parts.push(`Ödeme Durumu: ${filters.paymentStatus}`);
  if (filters.noteMode !== "Tümü") parts.push(`Not: ${filters.noteMode}`);
  if (filters.dateFrom) parts.push(`Başlangıç: ${formatDateLabel(filters.dateFrom)}`);
  if (filters.dateTo) parts.push(`Bitiş: ${formatDateLabel(filters.dateTo)}`);
  if (filters.minSale.trim()) parts.push(`Min Satış: ${filters.minSale.trim()}`);
  if (filters.maxSale.trim()) parts.push(`Max Satış: ${filters.maxSale.trim()}`);
  if (filters.minRemaining.trim()) parts.push(`Min Kalan: ${filters.minRemaining.trim()}`);
  if (filters.maxRemaining.trim()) parts.push(`Max Kalan: ${filters.maxRemaining.trim()}`);

  return parts.length > 0 ? parts.join(" • ") : "Aktif ek filtre yok";
}

function resolveStyle(type: SheetColumn<any>["type"], value: unknown) {
  if (type === "money") return "Money";
  if (type === "number") return "Number";
  if (type === "date") return "Date";
  if (typeof value === "number") return "Number";
  return "Cell";
}

function resolveDataType(type: SheetColumn<any>["type"], value: unknown) {
  if (type === "money" || type === "number") return "Number";
  if (typeof value === "number") return "Number";
  if (typeof value === "boolean") return "Boolean";
  return "String";
}

function getColumnWidths<T>(sheet: SheetDefinition<T>) {
  return sheet.columns.map((column) => {
    if (column.width) return column.width;

    const headerWidth = column.label.length;
    const valueWidth = sheet.rows.reduce((max, row) => {
      const next = String(column.value(row) ?? "").length;
      return Math.max(max, next);
    }, 0);

    return Math.max(70, Math.min(220, Math.max(headerWidth, valueWidth) * 7 + 18));
  });
}

function buildSheetXml<T>(sheet: SheetDefinition<T>, filtersLabel: string) {
  const columnWidths = getColumnWidths(sheet);
  const mergeAcross = Math.max(sheet.columns.length - 1, 0);

  const titleRow = `<Row ss:Height="24"><Cell ss:StyleID="Title" ss:MergeAcross="${mergeAcross}"><Data ss:Type="String">${xmlEscape(sheet.title)}</Data></Cell></Row>`;
  const subtitleRow = `<Row ss:Height="18"><Cell ss:StyleID="Subtitle" ss:MergeAcross="${mergeAcross}"><Data ss:Type="String">${xmlEscape(sheet.subtitle ?? filtersLabel)}</Data></Cell></Row>`;
  const emptyRow = `<Row ss:Height="8"></Row>`;

  const headerRow = `<Row ss:Height="22">${sheet.columns
    .map(
      (column) =>
        `<Cell ss:StyleID="Header"><Data ss:Type="String">${xmlEscape(column.label)}</Data></Cell>`
    )
    .join("")}</Row>`;

  const bodyRows = sheet.rows
    .map((row) => {
      const cells = sheet.columns
        .map((column) => {
          const value = column.value(row);
          const dataType = resolveDataType(column.type, value);
          const styleId = resolveStyle(column.type, value);
          return `<Cell ss:StyleID="${styleId}"><Data ss:Type="${dataType}">${xmlEscape(value ?? "")}</Data></Cell>`;
        })
        .join("");

      return `<Row>${cells}</Row>`;
    })
    .join("");

  return `
    <Worksheet ss:Name="${xmlEscape(sheet.name)}">
      <Table>
        ${columnWidths.map((width) => `<Column ss:AutoFitWidth="0" ss:Width="${width}"/>`).join("")}
        ${titleRow}
        ${subtitleRow}
        ${emptyRow}
        ${headerRow}
        ${bodyRows}
      </Table>
      <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
        <FreezePanes/>
        <FrozenNoSplit/>
        <SplitHorizontal>4</SplitHorizontal>
        <TopRowBottomPane>4</TopRowBottomPane>
        <ActivePane>2</ActivePane>
        <ProtectObjects>False</ProtectObjects>
        <ProtectScenarios>False</ProtectScenarios>
      </WorksheetOptions>
    </Worksheet>`;
}

function downloadWorkbook(filename: string, sheetsXml: string) {
  const workbookXml = `<?xml version="1.0"?>
  <?mso-application progid="Excel.Sheet"?>
  <Workbook
    xmlns="urn:schemas-microsoft-com:office:spreadsheet"
    xmlns:o="urn:schemas-microsoft-com:office:office"
    xmlns:x="urn:schemas-microsoft-com:office:excel"
    xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
    xmlns:html="http://www.w3.org/TR/REC-html40">
    <Styles>
      <Style ss:ID="Default" ss:Name="Normal">
        <Alignment ss:Vertical="Center"/>
        <Borders/>
        <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#3C2F26"/>
        <Interior/>
        <NumberFormat/>
        <Protection/>
      </Style>
      <Style ss:ID="Title">
        <Font ss:FontName="Calibri" ss:Bold="1" ss:Size="16" ss:Color="#FFFFFF"/>
        <Interior ss:Color="#4A3426" ss:Pattern="Solid"/>
        <Alignment ss:Vertical="Center" ss:WrapText="1"/>
      </Style>
      <Style ss:ID="Subtitle">
        <Font ss:FontName="Calibri" ss:Italic="1" ss:Size="10" ss:Color="#5E5247"/>
        <Interior ss:Color="#F3E7D7" ss:Pattern="Solid"/>
        <Alignment ss:Vertical="Center" ss:WrapText="1"/>
      </Style>
      <Style ss:ID="Header">
        <Font ss:FontName="Calibri" ss:Bold="1" ss:Size="10" ss:Color="#FFFFFF"/>
        <Interior ss:Color="#6A4B36" ss:Pattern="Solid"/>
        <Alignment ss:Vertical="Center" ss:WrapText="1"/>
        <Borders>
          <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D8C2AE"/>
          <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D8C2AE"/>
          <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D8C2AE"/>
          <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D8C2AE"/>
        </Borders>
      </Style>
      <Style ss:ID="Cell">
        <Alignment ss:Vertical="Center" ss:WrapText="1"/>
        <Borders>
          <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
        </Borders>
      </Style>
      <Style ss:ID="Number">
        <Alignment ss:Vertical="Center"/>
        <Borders>
          <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
        </Borders>
        <NumberFormat ss:Format="#\,##0"/>
      </Style>
      <Style ss:ID="Money">
        <Alignment ss:Vertical="Center"/>
        <Borders>
          <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
        </Borders>
        <NumberFormat ss:Format="#\,##0.00"/>
      </Style>
      <Style ss:ID="Date">
        <Alignment ss:Vertical="Center"/>
        <Borders>
          <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
          <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#EEE0D2"/>
        </Borders>
      </Style>
    </Styles>
    ${sheetsXml}
  </Workbook>`;

  const blob = new Blob([workbookXml], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".xls") ? filename : `${filename}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function buildSheetsXml<T>(sheets: Array<SheetDefinition<T>>, filters: ReportFilters) {
  const filtersLabel = `Oluşturulma: ${formatDateLabel(new Date().toISOString())} • ${getActiveFiltersLabel(filters)}`;
  return sheets.map((sheet) => buildSheetXml(sheet, filtersLabel)).join("\n");
}

function animalSummaryRows(animals: AnimalItem[]) {
  return animals.map((animal) => {
    const saleType = (animal as AnimalItem & { disKesim?: boolean }).disKesim
      ? "Dış Kesim"
      : animal.hisseliSatisEtiketi
        ? "Hisseli"
        : "Hissesiz";

    const soldShareCount = animal.hisseler.filter((share) => String(share.musteriAdi ?? "").trim().length > 0).length;
    const totalPaid = animal.hisseler.reduce((sum, share) => sum + Number(share.odenen ?? 0), 0);
    const totalRemaining = animal.hisseler.reduce((sum, share) => sum + Number(share.kalan ?? 0), 0);
    const totalSale = animal.hisseler.reduce((sum, share) => sum + getShareTotal(share), 0);

    return {
      kupeNo: getDisplayKupeNo(animal),
      siparisNo: getDisplaySiparisNo(animal),
      tur: animal.tur,
      satisTipi: saleType,
      kg: Number(animal.kg ?? 0),
      ref: Number(animal.referansFiyat ?? 0),
      toplamHisse: Number(animal.toplamHisse ?? 0),
      satilanHisse: soldShareCount,
      toplamSatis: totalSale,
      toplamOdenen: totalPaid,
      toplamKalan: totalRemaining,
      durum: animal.durum,
      kesimGunu: animal.kesimGunu ?? "-",
    };
  });
}

function shareDetailRows(animals: AnimalItem[]) {
  return animals.flatMap((animal) => {
    const satisTipi = (animal as AnimalItem & { disKesim?: boolean }).disKesim
      ? "Dış Kesim"
      : animal.hisseliSatisEtiketi
        ? "Hisseli"
        : "Hissesiz";

    return animal.hisseler.map((share) => ({
      kupeNo: getDisplayKupeNo(animal),
      siparisNo: getDisplaySiparisNo(animal),
      tur: animal.tur,
      satisTipi,
      hisseNo: share.hisseNo,
      musteri: share.musteriAdi || "-",
      telefon: share.telefon || "-",
      hisseAdedi: Number(share.hisseSayisi ?? 0),
      satisTutari: getShareTotal(share),
      odenen: Number(share.odenen ?? 0),
      kalan: Number(share.kalan ?? 0),
      kesim: Number(share.kesimUcreti ?? 0),
      parcalama: Number(share.parcalamaUcreti ?? 0),
      tasima: Number(share.tasimaUcreti ?? 0),
      satisNotu: share.satisNotu || share.pesinatAciklama || "-",
      fisNo: share.satisFisNo || "-",
      satisTarihi: formatDateLabel(share.satisTarihi),
    }));
  });
}

function financeSummaryRows(report: ReportsAggregateResult) {
  return [
    { baslik: "Toplam Satış", deger: report.overview.totalSale },
    { baslik: "Toplam Tahsilat", deger: report.overview.totalPaid },
    { baslik: "Toplam Kalan", deger: report.overview.totalRemaining },
    { baslik: "Toplam Gelir", deger: report.overview.totalIncome },
    { baslik: "Toplam Gider", deger: report.overview.totalExpense },
    { baslik: "Kâr / Zarar", deger: report.overview.profit },
    { baslik: "Borçlu Müşteri", deger: report.paymentSummary.debtors },
    { baslik: "Eksi Bakiye", deger: report.paymentSummary.negativeBalanceCount },
  ];
}

function buildAnimalLookup(animals: AnimalItem[]) {
  return new Map(animals.map((animal) => [Number(animal.id), animal]));
}

function getPaymentAnimalType(payment: PaymentRecord, animalLookup: Map<number, AnimalItem>) {
  const animal = animalLookup.get(Number(payment.animalId));
  if (!animal) return payment.animalId === 0 ? "Hisse Ön Satış" : "—";
  return animal.tur === "Büyükbaş" ? "B.Baş" : "K.Baş";
}

function getPaymentDisplayKupeNo(payment: PaymentRecord, animalLookup: Map<number, AnimalItem>) {
  const animal = animalLookup.get(Number(payment.animalId));
  if (animal) return getDisplayKupeNo(animal);

  // Legacy ödeme kayıtlarında hayvanKupeNo alanına ekrandaki Sipariş No,
  // hayvanSiparisNo alanına ekrandaki Küpe No yazılmış olabiliyor.
  // Rapor kolonlarında başlık-veri karışmaması için fallback'i ekran etiketine göre çeviriyoruz.
  return payment.hayvanSiparisNo || payment.hayvanKupeNo || "—";
}

function getPaymentDisplaySiparisNo(payment: PaymentRecord, animalLookup: Map<number, AnimalItem>) {
  const animal = animalLookup.get(Number(payment.animalId));
  if (animal) return getDisplaySiparisNo(animal);

  // Bkz. getPaymentDisplayKupeNo notu.
  return payment.hayvanKupeNo || payment.hayvanSiparisNo || "—";
}

function exportWorkbookFile(filenameBase: string, sheetsXml: string) {
  const today = new Date().toISOString().slice(0, 10);
  downloadWorkbook(`${normalizeFileName(filenameBase)}-${today}.xls`, sheetsXml);
}

export function downloadManagementWorkbook(report: ReportsAggregateResult, filters: ReportFilters) {
  const animalLookup = buildAnimalLookup(report.filteredAnimals);
  const overviewRows = [
    { baslik: "Toplam Hayvan", deger: report.overview.totalAnimals },
    { baslik: "Toplam Hisse", deger: report.overview.totalShares },
    { baslik: "Satılan Hisse", deger: report.overview.soldShares },
    { baslik: "Boş Hisse", deger: report.overview.emptyShares },
    { baslik: "Toplam Satış", deger: report.overview.totalSale },
    { baslik: "Toplam Tahsilat", deger: report.overview.totalPaid },
    { baslik: "Toplam Kalan", deger: report.overview.totalRemaining },
    { baslik: "Gelir", deger: report.overview.totalIncome },
    { baslik: "Gider", deger: report.overview.totalExpense },
    { baslik: "Kâr / Zarar", deger: report.overview.profit },
    { baslik: "Taşıma Bekleyen", deger: report.overview.transportWaiting },
    { baslik: "Teslim Edilen", deger: report.overview.deliveredCount },
  ];

  const sheets: Array<SheetDefinition<any>> = [
    {
      name: "Özet",
      title: "Yönetim Özeti",
      subtitle: "Satış, tahsilat ve operasyon ana göstergeleri",
      columns: [
        { label: "Başlık", value: (row) => row.baslik },
        { label: "Değer", type: "money", value: (row) => row.deger },
      ],
      rows: overviewRows,
    },
    {
      name: "Satışlar",
      title: "Satış Hareketleri",
      subtitle: "Filtreye uyan satış kayıtları",
      columns: [
        { label: "Müşteri", value: (row: SaleReportRow) => row.customerName },
        { label: "Küpe", value: (row) => row.kupeNo },
        { label: "Sipariş", value: (row) => row.siparisNo },
        { label: "Tür", value: (row) => row.animalType === "Büyükbaş" ? "B.Baş" : "K.Baş" },
        { label: "Hisse", value: (row) => row.shareNo },
        { label: "Satış Tipi", value: (row) => row.saleType },
        { label: "Satış", type: "money", value: (row) => row.saleAmount },
        { label: "Ödenen", type: "money", value: (row) => row.paidAmount },
        { label: "Kalan", type: "money", value: (row) => row.remainingAmount },
        { label: "Satış Notu", value: (row) => row.note || "-" },
      ],
      rows: report.filteredSales,
    },
    {
      name: "Tahsilatlar",
      title: "Tahsilat Hareketleri",
      subtitle: "Filtreye uyan ödeme kayıtları",
      columns: [
        { label: "Tarih", type: "date", value: (row: PaymentRecord) => formatDateLabel(row.tarih) },
        { label: "Müşteri", value: (row) => row.musteriAdi },
        { label: "Tür", value: (row) => getPaymentAnimalType(row, animalLookup) },
        { label: "Küpe", value: (row) => getPaymentDisplayKupeNo(row, animalLookup) },
        { label: "Sipariş", value: (row) => getPaymentDisplaySiparisNo(row, animalLookup) },
        { label: "Fiş No", value: (row) => row.fisNo || "-" },
        { label: "Ödeme Tipi", value: (row) => row.tip },
        { label: "Tutar", type: "money", value: (row) => Number(row.tutar ?? 0) },
        { label: "Kullanıcı", value: (row) => row.olusturan },
      ],
      rows: report.filteredPayments,
    },
    {
      name: "Müşteriler",
      title: "Müşteri Borç Özeti",
      subtitle: "Müşteri bazlı toplam satış / ödenen / kalan",
      columns: [
        { label: "Müşteri", value: (row: CustomerDebtRow) => row.customerName },
        { label: "Telefon", value: (row) => row.phone },
        { label: "Toplam Satış", type: "money", value: (row) => row.totalSale },
        { label: "Ödenen", type: "money", value: (row) => row.totalPaid },
        { label: "Kalan", type: "money", value: (row) => row.totalRemaining },
        { label: "Hisse", type: "number", value: (row) => row.shareCount },
        { label: "Not Sayısı", type: "number", value: (row) => row.noteCount },
      ],
      rows: report.filteredCustomers,
    },
  ];

  exportWorkbookFile("yonetim-raporu", buildSheetsXml(sheets, filters));
}


function sharePresaleRows(rows: SharePresaleRecord[]) {
  return rows.map((sale) => ({
    tarih: formatDateLabel(sale.tarih),
    satisNo: sale.satisNo,
    musteri: sale.musteriAdi,
    telefon: sale.telefon,
    paket: sale.paketBaslik,
    not: sale.not ?? "",
    tur: "Büyükbaş",
    kupeNo: sale.linkedKupeNo ?? "",
    siparisNo: sale.linkedSiparisNo ?? "",
    grup: sale.grupNo ? `Grup ${sale.grupNo}` : "",
    tasima: sale.tasima ? "Var" : "Yok",
    adet: Number(sale.adet ?? 0),
    toplam: Number(sale.toplamTutar ?? 0),
    kapora: Number(sale.kaporaTutar ?? 0),
    kalan: Number(sale.kalanTutar ?? 0),
    odeme: sale.odemeTipi ?? "Nakit",
    durum: sale.durum,
  }));
}

export function downloadSharePresaleWorkbook(rows: SharePresaleRecord[], filters: ReportFilters) {
  const sheets: Array<SheetDefinition<any>> = [
    {
      name: "Hisseli Satış Raporu",
      title: "Hisseli Satış Raporu",
      subtitle: "Hisse ön satış listesi",
      columns: [
        { label: "Tarih", value: (row) => row.tarih },
        { label: "Satış No", value: (row) => row.satisNo },
        { label: "Müşteri", value: (row) => row.musteri },
        { label: "Telefon", value: (row) => row.telefon },
        { label: "Paket", value: (row) => row.paket },
        { label: "Not", value: (row) => row.not },
        { label: "Tür", value: (row) => row.tur },
        { label: "Küpe No", value: (row) => row.kupeNo },
        { label: "Sipariş No", value: (row) => row.siparisNo },
        { label: "Grup", value: (row) => row.grup },
        { label: "Taşıma", value: (row) => row.tasima },
        { label: "Adet", type: "number", value: (row) => row.adet },
        { label: "Toplam", type: "money", value: (row) => row.toplam },
        { label: "Kapora", type: "money", value: (row) => row.kapora },
        { label: "Kalan", type: "money", value: (row) => row.kalan },
        { label: "Ödeme", value: (row) => row.odeme },
        { label: "Durum", value: (row) => row.durum },
      ],
      rows: sharePresaleRows(rows),
    },
  ];

  exportWorkbookFile("hisseli-satis-raporu", buildSheetsXml(sheets, filters));
}

export function downloadAnimalWorkbook(
  report: ReportsAggregateResult,
  filters: ReportFilters,
) {
  const animalRows = animalSummaryRows(report.filteredAnimals);
  const shareRows = shareDetailRows(report.filteredAnimals);

  const sheets: Array<SheetDefinition<any>> = [
    {
      name: "Hayvan Ana Liste",
      title: "Hayvan Ana Liste",
      subtitle: "Satış ve operasyon ana görünümü",
      columns: [
        { label: "Küpe No", value: (row) => row.kupeNo },
        { label: "Sipariş No", value: (row) => row.siparisNo },
        { label: "Tür", value: (row) => row.tur },
        { label: "Satış Tipi", value: (row) => row.satisTipi },
        { label: "KG", type: "number", value: (row) => row.kg },
        { label: "Ref Fiyat", type: "money", value: (row) => row.ref },
        { label: "Toplam Hisse", type: "number", value: (row) => row.toplamHisse },
        { label: "Satılan Hisse", type: "number", value: (row) => row.satilanHisse },
        { label: "Toplam Satış", type: "money", value: (row) => row.toplamSatis },
        { label: "Ödenen", type: "money", value: (row) => row.toplamOdenen },
        { label: "Kalan", type: "money", value: (row) => row.toplamKalan },
        { label: "Durum", value: (row) => row.durum },
        { label: "Kesim Günü", value: (row) => row.kesimGunu },
      ],
      rows: animalRows,
    },
    {
      name: "Hisse Detayları",
      title: "Hisse Detayları",
      subtitle: "Her hisse için müşteri ve ödeme görünümü",
      columns: [
        { label: "Küpe No", value: (row) => row.kupeNo },
        { label: "Sipariş No", value: (row) => row.siparisNo },
        { label: "Tür", value: (row) => row.tur },
        { label: "Satış Tipi", value: (row) => row.satisTipi },
        { label: "Hisse No", value: (row) => row.hisseNo },
        { label: "Müşteri", value: (row) => row.musteri },
        { label: "Telefon", value: (row) => row.telefon },
        { label: "Hisse Adedi", type: "number", value: (row) => row.hisseAdedi },
        { label: "Satış", type: "money", value: (row) => row.satisTutari },
        { label: "Ödenen", type: "money", value: (row) => row.odenen },
        { label: "Kalan", type: "money", value: (row) => row.kalan },
        { label: "Kesim", type: "money", value: (row) => row.kesim },
        { label: "Parçalama", type: "money", value: (row) => row.parcalama },
        { label: "Taşıma", type: "money", value: (row) => row.tasima },
        { label: "Satış Notu", value: (row) => row.satisNotu },
        { label: "Fiş No", value: (row) => row.fisNo },
        { label: "Satış Tarihi", value: (row) => row.satisTarihi },
      ],
      rows: shareRows,
    },
    {
      name: "Satış Dağılımı",
      title: "Hayvan Satış Dağılımı",
      subtitle: "Hayvanlara göre satış doluluk özeti",
      columns: [
        { label: "Küpe No", value: (row) => row.kupeNo },
        { label: "Sipariş No", value: (row) => row.siparisNo },
        { label: "Tür", value: (row) => row.tur },
        { label: "Satış Tipi", value: (row) => row.satisTipi },
        { label: "Satılan Hisse", type: "number", value: (row) => row.satilanHisse },
        { label: "Toplam Hisse", type: "number", value: (row) => row.toplamHisse },
        { label: "Kalan", type: "money", value: (row) => row.toplamKalan },
      ],
      rows: animalRows,
    },
  ];

  exportWorkbookFile("hayvan-raporu", buildSheetsXml(sheets, filters));
}

export function downloadCustomerWorkbook(report: ReportsAggregateResult, filters: ReportFilters) {
  const animalLookup = buildAnimalLookup(report.filteredAnimals);
  const sheets: Array<SheetDefinition<any>> = [
    {
      name: "Müşteri Liste",
      title: "Müşteri Liste",
      subtitle: "Müşteri bazlı genel özet",
      columns: [
        { label: "Müşteri", value: (row: CustomerDebtRow) => row.customerName },
        { label: "Telefon", value: (row) => row.phone },
        { label: "Toplam Satış", type: "money", value: (row) => row.totalSale },
        { label: "Ödenen", type: "money", value: (row) => row.totalPaid },
        { label: "Kalan", type: "money", value: (row) => row.totalRemaining },
        { label: "Hisse", type: "number", value: (row) => row.shareCount },
        { label: "Son Fiş", value: (row) => row.lastReceiptNo },
      ],
      rows: report.filteredCustomers,
    },
    {
      name: "Satın Aldıkları",
      title: "Müşteri Satışları",
      subtitle: "Müşterinin satın aldığı hisse / hayvan satırları",
      columns: [
        { label: "Müşteri", value: (row: SaleReportRow) => row.customerName },
        { label: "Tür", value: (row) => row.animalType === "Büyükbaş" ? "B.Baş" : "K.Baş" },
        { label: "Küpe", value: (row) => row.kupeNo },
        { label: "Sipariş", value: (row) => row.siparisNo },
        { label: "Hisse", value: (row) => row.shareNo },
        { label: "Satış Tipi", value: (row) => row.saleType },
        { label: "Satış", type: "money", value: (row) => row.saleAmount },
        { label: "Ödenen", type: "money", value: (row) => row.paidAmount },
        { label: "Kalan", type: "money", value: (row) => row.remainingAmount },
        { label: "Satış Notu", value: (row) => row.note || "-" },
      ],
      rows: report.filteredSales,
    },
    {
      name: "Ödemeler",
      title: "Müşteri Ödemeleri",
      subtitle: "Müşterilere ait ödeme hareketleri",
      columns: [
        { label: "Tarih", type: "date", value: (row: PaymentRecord) => formatDateLabel(row.tarih) },
        { label: "Müşteri", value: (row) => row.musteriAdi },
        { label: "Tür", value: (row) => getPaymentAnimalType(row, animalLookup) },
        { label: "Küpe", value: (row) => getPaymentDisplayKupeNo(row, animalLookup) },
        { label: "Sipariş", value: (row) => getPaymentDisplaySiparisNo(row, animalLookup) },
        { label: "Fiş No", value: (row) => row.fisNo || "-" },
        { label: "Tutar", type: "money", value: (row) => Number(row.tutar ?? 0) },
        { label: "Tip", value: (row) => row.tip },
        { label: "Açıklama", value: (row) => row.aciklama || "-" },
      ],
      rows: report.filteredPayments,
    },
  ];

  exportWorkbookFile("musteri-raporu", buildSheetsXml(sheets, filters));
}

export function downloadFinanceWorkbook(report: ReportsAggregateResult, filters: ReportFilters) {
  const animalLookup = buildAnimalLookup(report.filteredAnimals);
  const sheets: Array<SheetDefinition<any>> = [
    {
      name: "Finans Özet",
      title: "Finans Özet",
      subtitle: "Gelir, gider, tahsilat ve kâr görünümü",
      columns: [
        { label: "Başlık", value: (row) => row.baslik },
        { label: "Değer", type: "money", value: (row) => row.deger },
      ],
      rows: financeSummaryRows(report),
    },
    {
      name: "Tahsilat",
      title: "Tahsilat Listesi",
      subtitle: "Filtreye uyan ödeme hareketleri",
      columns: [
        { label: "Tarih", type: "date", value: (row: PaymentRecord) => formatDateLabel(row.tarih) },
        { label: "Müşteri", value: (row) => row.musteriAdi },
        { label: "Tür", value: (row) => getPaymentAnimalType(row, animalLookup) },
        { label: "Küpe", value: (row) => getPaymentDisplayKupeNo(row, animalLookup) },
        { label: "Sipariş", value: (row) => getPaymentDisplaySiparisNo(row, animalLookup) },
        { label: "Fiş No", value: (row) => row.fisNo || "-" },
        { label: "Tutar", type: "money", value: (row) => Number(row.tutar ?? 0) },
        { label: "Tip", value: (row) => row.tip },
        { label: "Kullanıcı", value: (row) => row.olusturan },
      ],
      rows: report.filteredPayments,
    },
    {
      name: "Gelir Gider",
      title: "Gelir / Gider Hareketleri",
      subtitle: "Panel finans kayıtları",
      columns: [
        { label: "Tarih", type: "date", value: (row: IncomeExpenseEntry) => formatDateLabel(row.tarih) },
        { label: "Tür", value: (row) => row.tur },
        { label: "Kalem", value: (row) => row.kalemAdi },
        { label: "Tekrar", value: (row) => row.tekrarTipi },
        { label: "Tutar", type: "money", value: (row) => Number(row.tutar ?? 0) },
        { label: "Açıklama", value: (row) => row.aciklama || "-" },
      ],
      rows: report.filteredIncomeExpense,
    },
    {
      name: "Bakiye Listesi",
      title: "Müşteri Bakiye Listesi",
      subtitle: "Borçlu ve eksi bakiye kayıtları",
      columns: [
        { label: "Müşteri", value: (row: CustomerDebtRow) => row.customerName },
        { label: "Telefon", value: (row) => row.phone },
        { label: "Toplam Satış", type: "money", value: (row) => row.totalSale },
        { label: "Ödenen", type: "money", value: (row) => row.totalPaid },
        { label: "Kalan", type: "money", value: (row) => row.totalRemaining },
      ],
      rows: report.filteredCustomers,
    },
  ];

  exportWorkbookFile("finans-raporu", buildSheetsXml(sheets, filters));
}

export function downloadUserWorkbook(
  rows: UserPerformanceRow[],
  filters: ReportFilters,
) {
  const sheets: Array<SheetDefinition<any>> = [
    {
      name: "Kullanıcılar",
      title: "Kullanıcı Performans Raporu",
      subtitle: "Ödeme ve müşteri hareketlerine göre ekip görünümü",
      columns: [
        { label: "Kullanıcı", value: (row) => row.userName },
        { label: "Rol", value: (row) => row.role },
        { label: "Oluşturulan Müşteri", type: "number", value: (row) => row.createdCustomers },
        { label: "Ödeme Adedi", type: "number", value: (row) => row.paymentCount },
        { label: "Ödeme Tutarı", type: "money", value: (row) => row.paymentAmount },
      ],
      rows,
    },
  ];

  exportWorkbookFile("kullanici-raporu", buildSheetsXml(sheets, filters));
}


export function downloadAllReportsWorkbook(
  report: ReportsAggregateResult,
  filters: ReportFilters,
  userRows: UserPerformanceRow[],
  sharePresaleRecords: SharePresaleRecord[] = [],
) {
  const animalLookup = buildAnimalLookup(report.filteredAnimals);
  const animalRows = animalSummaryRows(report.filteredAnimals);
  const shareRows = shareDetailRows(report.filteredAnimals);

  const sheets: Array<SheetDefinition<any>> = [
    {
      name: "Yönetim Özet",
      title: "Yönetim Özet",
      subtitle: "Tüm raporlar tek dosya",
      columns: [
        { label: "Başlık", value: (row) => row.baslik },
        { label: "Değer", type: "money", value: (row) => row.deger },
      ],
      rows: [
        { baslik: "Toplam Hayvan", deger: report.overview.totalAnimals },
        { baslik: "Toplam Satış", deger: report.overview.totalSale },
        { baslik: "Toplam Tahsilat", deger: report.overview.totalPaid },
        { baslik: "Toplam Kalan", deger: report.overview.totalRemaining },
        { baslik: "Gelir", deger: report.overview.totalIncome },
        { baslik: "Gider", deger: report.overview.totalExpense },
        { baslik: "Kâr / Zarar", deger: report.overview.profit },
      ],
    },
    {
      name: "Hayvan Ana Liste",
      title: "Hayvan Ana Liste",
      columns: [
        { label: "Küpe No", value: (row) => row.kupeNo },
        { label: "Sipariş No", value: (row) => row.siparisNo },
        { label: "Tür", value: (row) => row.tur },
        { label: "Satış Tipi", value: (row) => row.satisTipi },
        { label: "Toplam Hisse", type: "number", value: (row) => row.toplamHisse },
        { label: "Satılan Hisse", type: "number", value: (row) => row.satilanHisse },
        { label: "Toplam Satış", type: "money", value: (row) => row.toplamSatis },
        { label: "Ödenen", type: "money", value: (row) => row.toplamOdenen },
        { label: "Kalan", type: "money", value: (row) => row.toplamKalan },
        { label: "Kesim Günü", value: (row) => row.kesimGunu },
      ],
      rows: animalRows,
    },
    {
      name: "Hisse Detayları",
      title: "Hisse Detayları",
      columns: [
        { label: "Küpe No", value: (row) => row.kupeNo },
        { label: "Sipariş No", value: (row) => row.siparisNo },
        { label: "Tür", value: (row) => row.tur },
        { label: "Satış Tipi", value: (row) => row.satisTipi },
        { label: "Hisse No", value: (row) => row.hisseNo },
        { label: "Müşteri", value: (row) => row.musteri },
        { label: "Telefon", value: (row) => row.telefon },
        { label: "Satış", type: "money", value: (row) => row.satisTutari },
        { label: "Ödenen", type: "money", value: (row) => row.odenen },
        { label: "Kalan", type: "money", value: (row) => row.kalan },
        { label: "Satış Notu", value: (row) => row.satisNotu },
      ],
      rows: shareRows,
    },
    {
      name: "Hisseli Satış Raporu",
      title: "Hisseli Satış Raporu",
      columns: [
        { label: "Tarih", value: (row) => row.tarih },
        { label: "Satış No", value: (row) => row.satisNo },
        { label: "Müşteri", value: (row) => row.musteri },
        { label: "Telefon", value: (row) => row.telefon },
        { label: "Paket", value: (row) => row.paket },
        { label: "Not", value: (row) => row.not },
        { label: "Tür", value: (row) => row.tur },
        { label: "Küpe No", value: (row) => row.kupeNo },
        { label: "Sipariş No", value: (row) => row.siparisNo },
        { label: "Grup", value: (row) => row.grup },
        { label: "Taşıma", value: (row) => row.tasima },
        { label: "Adet", type: "number", value: (row) => row.adet },
        { label: "Toplam", type: "money", value: (row) => row.toplam },
        { label: "Kapora", type: "money", value: (row) => row.kapora },
        { label: "Kalan", type: "money", value: (row) => row.kalan },
        { label: "Durum", value: (row) => row.durum },
      ],
      rows: sharePresaleRows(sharePresaleRecords),
    },
    {
      name: "Müşteriler",
      title: "Müşteri Borç Özeti",
      columns: [
        { label: "Müşteri", value: (row: CustomerDebtRow) => row.customerName },
        { label: "Telefon", value: (row) => row.phone },
        { label: "Toplam Satış", type: "money", value: (row) => row.totalSale },
        { label: "Ödenen", type: "money", value: (row) => row.totalPaid },
        { label: "Kalan", type: "money", value: (row) => row.totalRemaining },
        { label: "Hisse", type: "number", value: (row) => row.shareCount },
      ],
      rows: report.filteredCustomers,
    },
    {
      name: "Ödemeler",
      title: "Ödemeler",
      columns: [
        { label: "Tarih", type: "date", value: (row: PaymentRecord) => formatDateLabel(row.tarih) },
        { label: "Müşteri", value: (row) => row.musteriAdi },
        { label: "Tür", value: (row) => getPaymentAnimalType(row, animalLookup) },
        { label: "Küpe", value: (row) => getPaymentDisplayKupeNo(row, animalLookup) },
        { label: "Sipariş", value: (row) => getPaymentDisplaySiparisNo(row, animalLookup) },
        { label: "Hisse", value: (row) => row.hisseNo },
        { label: "Fiş No", value: (row) => row.fisNo || row.havaleNo || "-" },
        { label: "Tip", value: (row) => row.tip },
        { label: "Tutar", type: "money", value: (row) => Number(row.tutar ?? 0) },
      ],
      rows: report.filteredPayments,
    },
    {
      name: "Gelir Gider",
      title: "Gelir / Gider",
      columns: [
        { label: "Tarih", type: "date", value: (row: IncomeExpenseEntry) => formatDateLabel(row.tarih) },
        { label: "Tür", value: (row) => row.tur },
        { label: "Kalem", value: (row) => row.kalemAdi },
        { label: "Tutar", type: "money", value: (row) => Number(row.tutar ?? 0) },
        { label: "Açıklama", value: (row) => row.aciklama || "-" },
      ],
      rows: report.filteredIncomeExpense,
    },
    {
      name: "Kullanıcılar",
      title: "Kullanıcı Performansı",
      columns: [
        { label: "Kullanıcı", value: (row: UserPerformanceRow) => row.userName },
        { label: "Rol", value: (row) => row.role },
        { label: "Ödeme Adedi", type: "number", value: (row) => row.paymentCount },
        { label: "Ödeme Tutarı", type: "money", value: (row) => row.paymentAmount },
      ],
      rows: userRows,
    },
  ];

  exportWorkbookFile("tum-raporlar", buildSheetsXml(sheets, filters));
}
